import React, { useState } from 'react';
import AMPageShell from './components/AMPageShell';
import ReviewStatusBadge from './components/ReviewStatusBadge';
import Icon from 'components/AppIcon';
import { OVERSIGHT_QUEUE as OVERSIGHT_QUEUE_DEFAULT, AUDIT_LOG, ASSETS } from './data/assetData';
import { useNavigate } from 'react-router-dom';

// ─── Review Sign-off Chain ────────────────────────────────────────────────────
function ReviewSignoffChain({ reviewChain }) {
  if (!reviewChain?.length) return null;
  return (
    <div className="mt-3 mb-3 pt-3 border-t border-border">
      <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
        Sign-off Chain
      </p>
      <div className="space-y-1.5">
        {reviewChain.map((step, i) => (
          <div key={i} className="flex items-center gap-3">
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
              style={{ background: step.signed ? '#059669' : 'var(--color-muted-foreground)' }}
            >
              {step.signed ? <Icon name="Check" size={11} /> : step.initials}
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-caption text-xs font-semibold text-foreground">{step.name}</span>
              <span className="font-caption text-xs text-muted-foreground ml-1.5">{step.role}</span>
            </div>
            <span
              className="font-caption text-xs font-semibold flex-shrink-0"
              style={{ color: step.signed ? '#059669' : '#d97706' }}
            >
              {step.signed ? `Signed ${step.signedDate}` : 'Pending'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const TRIGGER_COLOURS = {
  'Performance variance': 'bg-warning/10 text-warning',
  'Periodic review': 'bg-primary/10 text-primary',
  'Reserved matter': 'bg-purple-100 text-purple-700',
  'Counterparty issue': 'bg-orange-100 text-orange-700',
  'Distress signal': 'bg-error/10 text-error',
};

const DECISION_BUTTONS = [
  { label: 'Confirm', icon: 'CheckCircle', colour: 'var(--color-success)', outcome: 'Confirmed' },
  { label: 'Confirm with Actions', icon: 'ClipboardCheck', colour: 'var(--color-primary)', outcome: 'Confirmed with actions' },
  { label: 'Move to Watchlist', icon: 'AlertCircle', colour: 'var(--color-warning)', outcome: 'Watchlist' },
  { label: 'Escalate', icon: 'AlertTriangle', colour: 'var(--color-error)', outcome: 'Escalated' },
  { label: 'Recommend Exit', icon: 'LogOut', colour: 'var(--color-primary)', outcome: 'Exit case' },
];

const WEEKLY_MEETINGS = [
  { date: '15 Jan 2026', time: '09:00', type: 'Oversight Committee', items: 3, chair: 'J. Donovan' },
  { date: '05 Feb 2026', time: '09:00', type: 'Oversight Committee', items: 2, chair: 'J. Donovan' },
  { date: '05 Mar 2026', time: '09:00', type: 'Oversight Committee', items: 3, chair: 'J. Donovan' },
];

export default function OversightHub({ selectedAssets, showToast, oversightQueue: oversightQueueProp, setOversightQueue }) {
  const oversightQueue = oversightQueueProp || OVERSIGHT_QUEUE_DEFAULT;
  const [decisions, setDecisions] = useState({});
  const navigate = useNavigate();
  const [auditLog, setAuditLog] = useState(AUDIT_LOG);

  const handleDecision = (packId, assetName, outcome) => {
    setDecisions((p) => ({ ...p, [packId]: outcome }));
    const entry = {
      id: `AL-${Date.now()}`,
      timestamp: new Date()?.toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }),
      user: 'J. Donovan',
      action: 'Oversight decision recorded',
      asset: assetName,
      detail: `Decision: ${outcome}`,
    };
    setAuditLog((p) => [entry, ...p]);
    showToast?.(`Decision recorded: ${outcome} — ${assetName}`);
  };

  return (
    <AMPageShell activeTab="/asset-management/oversight-hub">
      <div className="space-y-8">
        {/* Weekly Meeting Scheduler */}
        <div className="bg-card border border-border rounded-lg shadow-xs overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center gap-2">
            <Icon name="Calendar" size={16} color="var(--color-primary)" strokeWidth={2} />
            <h2 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.1rem' }}>Oversight Committee Schedule</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-border">
            {WEEKLY_MEETINGS?.map((m, i) => (
              <div key={i} className="px-6 py-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center">
                    <Icon name="Calendar" size={15} color="var(--color-primary)" strokeWidth={2} />
                  </div>
                  <div>
                    <p className="font-caption text-sm font-semibold text-foreground">{m?.date}</p>
                    <p className="font-caption text-xs text-muted-foreground">{m?.time} · {m?.type}</p>
                  </div>
                </div>
                <p className="font-caption text-xs text-muted-foreground mt-2">Chair: {m?.chair}</p>
                <p className="font-caption text-xs text-muted-foreground">{m?.items} review pack{m?.items > 1 ? 's' : ''} queued</p>
              </div>
            ))}
          </div>
        </div>

        {/* Review Pack Queue */}
        <div className="bg-card border border-border rounded-lg shadow-xs overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.1rem' }}>Review Pack Queue</h2>
            <p className="font-caption text-xs text-muted-foreground mt-0.5">Packs submitted for next oversight meeting</p>
          </div>
          <div className="divide-y divide-border">
            {oversightQueue?.map((item) => {
              const asset = ASSETS?.find((a) => a?.id === item?.packId);
              return (
                <div key={item?.id} className="px-6 py-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <button
                          onClick={() => navigate(`/portfolio/assets/${item?.packId}`, { state: { from: 'asset-management', backPath: '/asset-management/oversight', backLabel: 'Oversight Hub' } })}
                          className="font-caption text-sm font-semibold text-primary hover:underline"
                        >
                          {item?.asset}
                        </button>
                        <ReviewStatusBadge status={item?.status} />
                        <span className={`text-xs px-2 py-0.5 rounded-full font-caption font-medium ${TRIGGER_COLOURS?.[item?.triggerReason] || 'bg-muted text-muted-foreground'}`}>
                          {item?.triggerReason}
                        </span>
                      </div>
                      <p className="font-caption text-xs text-muted-foreground">Review date: {item?.reviewDate} · Owner: {item?.owner}</p>
                      {decisions?.[item?.packId] && (
                        <p className="font-caption text-xs text-success mt-1 font-medium">✓ Decision recorded: {decisions?.[item?.packId]}</p>
                      )}
                    </div>
                  </div>
                  {asset?.reviewChain && (
                    <ReviewSignoffChain reviewChain={asset.reviewChain} />
                  )}
                  {!decisions?.[item?.packId] && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {DECISION_BUTTONS?.map((btn) => (
                        <button
                          key={btn?.label}
                          onClick={() => handleDecision(item?.packId, item?.asset, btn?.outcome)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-caption font-medium border border-border hover:border-current transition-all duration-250"
                          style={{ color: btn?.colour }}
                        >
                          <Icon name={btn?.icon} size={13} color={btn?.colour} strokeWidth={2} />
                          {btn?.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Audit Entries */}
        <div className="bg-card border border-border rounded-lg shadow-xs overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.1rem' }}>Recent Audit Entries</h2>
          </div>
          <div className="divide-y divide-border">
            {auditLog?.slice(0, 6)?.map((entry) => (
              <div key={entry?.id} className="px-6 py-3 flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                <div>
                  <p className="font-caption text-sm text-foreground"><span className="font-medium">{entry?.action}</span> · {entry?.asset}</p>
                  <p className="font-caption text-xs text-muted-foreground">{entry?.timestamp} · {entry?.user} · {entry?.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AMPageShell>
  );
}
